import React, { useEffect, useState, useRef, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, PhoneCall, Bot, Database, MessageSquare, Radio, AlertTriangle, RotateCcw, Play, Pause, Headphones, Sparkles, Layers, Zap, Clock, DollarSign } from 'lucide-react';
// =================== Stage coordinates ===================
const STAGE_W = 1280;
const STAGE_H = 720;
// Diagram center (pulled slightly up to leave clean room for support layer)
const CENTER = {
  x: 640,
  y: 320
};
// Card geometry for line-edge math
const CARD_W = 210;
const CARD_H = 72;
const CENTER_W = 230;
const CENTER_H = 86;
// System nodes — clean pentagon around the center, NO node at bottom (bottom belongs to support layer)
const SYSTEMS = [{
  id: 'pbx',
  label: 'PBX / Call Center',
  sub: 'Telephony backbone',
  icon: PhoneCall,
  x: 640,
  y: 130
}, {
  id: 'ai',
  label: 'AI Agent Provider',
  sub: 'LLM + Voice',
  icon: Bot,
  x: 980,
  y: 240,
  warning: true
}, {
  id: 'crm',
  label: 'CRM System',
  sub: 'Customer data',
  icon: Database,
  x: 980,
  y: 410
}, {
  id: 'whatsapp',
  label: 'WhatsApp API',
  sub: 'Messaging channel',
  icon: MessageSquare,
  x: 300,
  y: 410
}, {
  id: 'telecom',
  label: 'Telecom Provider',
  sub: 'Carrier & numbers',
  icon: Radio,
  x: 300,
  y: 240
}] as const;
// Support row — its own clean zone below the diagram
const SUPPORT_DIVIDER_Y = 555;
const SUPPORT_CHIPS = [{
  id: 'pbx-sup',
  label: 'PBX Support',
  x: 360,
  y: 625
}, {
  id: 'ai-sup',
  label: 'AI Vendor Support',
  x: 640,
  y: 625
}, {
  id: 'tel-sup',
  label: 'Telecom Support',
  x: 920,
  y: 625
}];
// Pain stats — bottom-left corner, away from all cards
const PAIN_STATS = [{
  icon: Layers,
  label: 'Vendors',
  value: '5'
}, {
  icon: Zap,
  label: 'Integrations',
  value: '12+'
}, {
  icon: Clock,
  label: 'Setup',
  value: '6 mo'
}, {
  icon: DollarSign,
  label: 'Monthly cost',
  value: 'High'
}];
// Compute the point on a card's edge along the line from CENTER → card-center,
// so connection lines visibly terminate at the card border instead of passing through it.
function edgePoint(cx: number, cy: number, tx: number, ty: number, halfW: number, halfH: number) {
  const dx = tx - cx;
  const dy = ty - cy;
  if (dx === 0 && dy === 0) return {
    x: tx,
    y: ty
  };
  // scale factor s such that |s*dx| <= halfW and |s*dy| <= halfH; pick the binding axis
  const sx = halfW / Math.abs(dx || 1e-9);
  const sy = halfH / Math.abs(dy || 1e-9);
  const s = Math.min(sx, sy);
  return {
    x: tx - dx * s,
    y: ty - dy * s
  };
}
// Pre-compute line endpoints (start = edge of center card, end = edge of system card)
const LINES = SYSTEMS.map(s => {
  const start = edgePoint(s.x, s.y, CENTER.x, CENTER.y, CENTER_W / 2, CENTER_H / 2);
  const end = edgePoint(CENTER.x, CENTER.y, s.x, s.y, CARD_W / 2, CARD_H / 2);
  const length = Math.hypot(end.x - start.x, end.y - start.y);
  return {
    id: s.id,
    start,
    end,
    length
  };
});
// =================== Scenes ===================
interface Scene {
  id: 'intro' | 'build' | 'support' | 'impact';
  chapter: string;
  title: string;
  caption: string;
  duration: number;
}
const SCENES: Scene[] = [{
  id: 'intro',
  chapter: 'Chapter 01',
  title: 'The Setup',
  caption: 'Today, enabling AI in your business…',
  duration: 5
}, {
  id: 'build',
  chapter: 'Chapter 02',
  title: 'Stitching It Together',
  caption: 'Five vendors. Twelve integrations. One business.',
  duration: 7
}, {
  id: 'support',
  chapter: 'Chapter 03',
  title: 'When Something Breaks',
  caption: 'One issue. Three support teams. Zero accountability.',
  duration: 6
}, {
  id: 'impact',
  chapter: 'Chapter 04',
  title: 'The Cost',
  caption: 'Three words. One painful reality.',
  duration: 6
}];
const TOTAL_DURATION = SCENES.reduce((acc, s) => acc + s.duration, 0);
// Absolute timing
const T = {
  CENTER_IN: 5.2,
  PBX: 6.0,
  AI: 6.7,
  CRM: 7.4,
  WHATSAPP: 8.1,
  TELECOM: 8.8,
  STATS: 10.0,
  SUPPORT: 12.5,
  WARN: 13.0,
  BOUNCE: 13.6,
  IMPACT: 18.5
};
const SYS_TIME: Record<string, number> = {
  pbx: T.PBX,
  ai: T.AI,
  crm: T.CRM,
  whatsapp: T.WHATSAPP,
  telecom: T.TELECOM
};
// =================== Main ===================
export function PresentationSlide1({
  externalElapsed,
  hideChrome
}: {
  externalElapsed?: number;
  hideChrome?: boolean;
} = {}) {
  const isControlled = externalElapsed !== undefined;
  const [runKey, setRunKey] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [internalElapsed, setElapsed] = useState(0);
  const startedAtRef = useRef<number>(Date.now());
  const pausedAtRef = useRef<number>(0);
  useEffect(() => {
    if (isControlled) return;
    startedAtRef.current = Date.now();
    pausedAtRef.current = 0;
    setElapsed(0);
  }, [runKey, isControlled]);
  useEffect(() => {
    if (isControlled) return;
    if (!playing) {
      pausedAtRef.current = internalElapsed;
      return;
    }
    startedAtRef.current = Date.now() - pausedAtRef.current * 1000;
    let raf: number;
    const tick = () => {
      const t = (Date.now() - startedAtRef.current) / 1000;
      setElapsed(Math.min(t, TOTAL_DURATION));
      if (t < TOTAL_DURATION) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, runKey, isControlled]);
  const elapsed = isControlled ? Math.max(0, Math.min(externalElapsed!, TOTAL_DURATION)) : internalElapsed;
  const currentScene = (() => {
    let acc = 0;
    for (const s of SCENES) {
      if (elapsed < acc + s.duration) return {
        scene: s,
        sceneStart: acc
      };
      acc += s.duration;
    }
    return {
      scene: SCENES[SCENES.length - 1],
      sceneStart: TOTAL_DURATION - SCENES[SCENES.length - 1].duration
    };
  })();
  return <div className="relative w-screen h-screen overflow-hidden bg-[#06080F] text-white font-sans flex items-center justify-center" data-id="element-1323">
      {/* Backgrounds */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#06080F] via-[#0A0F1F] to-[#06080F]" data-id="element-1324" />
      <motion.div className="absolute inset-0" animate={{
      background: ['radial-gradient(circle at 30% 40%, rgba(16,217,106,0.12) 0%, transparent 55%), radial-gradient(circle at 70% 70%, rgba(34,211,238,0.07) 0%, transparent 50%)', 'radial-gradient(circle at 70% 30%, rgba(16,217,106,0.12) 0%, transparent 55%), radial-gradient(circle at 30% 70%, rgba(34,211,238,0.07) 0%, transparent 50%)', 'radial-gradient(circle at 30% 40%, rgba(16,217,106,0.12) 0%, transparent 55%), radial-gradient(circle at 70% 70%, rgba(34,211,238,0.07) 0%, transparent 50%)']
    }} transition={{
      duration: 14,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-1325" />
      <div className="absolute inset-0 opacity-[0.10]" style={{
      backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.18) 1px, transparent 1px)',
      backgroundSize: '36px 36px'
    }} data-id="element-1326" />
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,0.7) 100%)'
    }} data-id="element-1327" />

      {/* Top controls — hidden when chrome is suppressed (deck owns controls) */}
      {!hideChrome && <div className="absolute top-6 right-6 z-50 flex items-center gap-2" data-id="element-1328">
          <button onClick={() => setPlaying(p => !p)} className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.05] hover:bg-white/[0.1] backdrop-blur-xl border border-white/10 rounded-full text-xs font-semibold text-white/80 hover:text-white transition-all" data-id="element-1329">
            {playing ? <>
                <Pause className="w-3.5 h-3.5" data-id="element-1330" /> Pause
              </> : <>
                <Play className="w-3.5 h-3.5" data-id="element-1331" /> Play
              </>}
          </button>
          <button onClick={() => {
        setRunKey(k => k + 1);
        setPlaying(true);
      }} className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.05] hover:bg-white/[0.1] backdrop-blur-xl border border-white/10 rounded-full text-xs font-semibold text-white/80 hover:text-white transition-all" data-id="element-1332">
            <RotateCcw className="w-3.5 h-3.5" data-id="element-1333" /> Replay
          </button>
        </div>}

      {/* Chapter indicator */}
      <AnimatePresence mode="wait" data-id="element-1334">
        <motion.div key={currentScene.scene.id} initial={{
        opacity: 0,
        x: -12
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -12
      }} transition={{
        duration: 0.6
      }} className="absolute top-6 left-6 z-50 hidden" data-id="element-1335">
          <div className="flex items-center gap-3" data-id="element-1336">
            <div className="w-8 h-px bg-emerald-400/70" data-id="element-1337" />
            <div data-id="element-1338">
              <div className="text-[10px] font-bold tracking-[0.3em] text-emerald-400 uppercase" data-id="element-1339">
                {currentScene.scene.chapter}
              </div>
              <div className="text-sm font-display text-white/90 tracking-tight mt-0.5" data-id="element-1340">
                {currentScene.scene.title}
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* INTRO TAKEOVER */}
      <AnimatePresence data-id="element-1341">
        {currentScene.scene.id === 'intro' && <IntroSequence sceneTime={elapsed} data-id="element-1342" />}
      </AnimatePresence>

      {/* DIAGRAM STAGE */}
      {currentScene.scene.id !== 'intro' && <Stage scene={currentScene.scene.id} elapsed={elapsed} caption={currentScene.scene.caption} captionKey={currentScene.scene.id} data-id="element-1343" />}

      {/* Bottom progress bar — hidden when chrome is suppressed */}
      {!hideChrome && <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl px-6" data-id="element-1344">
          <div className="flex items-center gap-2 mb-2.5" data-id="element-1345">
            {SCENES.map((s, i) => {
          const sceneStart = SCENES.slice(0, i).reduce((a, b) => a + b.duration, 0);
          const sceneEnd = sceneStart + s.duration;
          const isActive = elapsed >= sceneStart && elapsed < sceneEnd;
          const isDone = elapsed >= sceneEnd;
          return <div key={s.id} className="flex-1 h-1 rounded-full bg-white/[0.08] overflow-hidden" data-id="element-1346">
                  <motion.div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-300" animate={{
              width: isDone ? '100%' : isActive ? `${(elapsed - sceneStart) / s.duration * 100}%` : '0%'
            }} transition={{
              duration: 0.1,
              ease: 'linear'
            }} data-id="element-1347" />
                </div>;
        })}
          </div>
          <div className="flex items-center justify-between text-[9px] font-bold tracking-[0.25em] text-white/45 uppercase" data-id="element-1348">
            <span data-id="element-1349">Slide 01 · The Reality</span>
            <span className="font-mono" data-id="element-1350">
              {fmt(elapsed)} / {fmt(TOTAL_DURATION)}
            </span>
          </div>
        </div>}
    </div>;
}
function fmt(s: number) {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}
// =================== INTRO ===================
function IntroSequence({
  sceneTime
}: {
  sceneTime: number;
}) {
  const words = [{
    text: 'Today,',
    delay: 0.3
  }, {
    text: 'enabling',
    delay: 0.7
  }, {
    text: 'AI',
    delay: 1.1,
    accent: true
  }, {
    text: 'in',
    delay: 1.5
  }, {
    text: 'your',
    delay: 1.7
  }, {
    text: 'business…',
    delay: 2.0
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0,
    scale: 0.95,
    filter: 'blur(8px)'
  }} transition={{
    duration: 0.9
  }} className="absolute inset-0 z-30 flex flex-col items-center justify-center px-8" data-id="element-1351">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1,
      duration: 0.8
    }} className="flex items-center gap-3 mb-10" data-id="element-1352">
        <div className="w-8 h-px bg-emerald-400/60" data-id="element-1353" />
        <span className="text-[11px] font-bold tracking-[0.3em] text-emerald-400 uppercase" data-id="element-1354">
          The Reality
        </span>
        <div className="w-8 h-px bg-emerald-400/60" data-id="element-1355" />
      </motion.div>

      <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tight text-white text-center leading-[1.05] max-w-5xl" data-id="element-1356">
        {words.map((w, i) => <motion.span key={i} initial={{
        opacity: 0,
        y: 30,
        filter: 'blur(10px)'
      }} animate={{
        opacity: 1,
        y: 0,
        filter: 'blur(0px)'
      }} transition={{
        delay: w.delay,
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} className="inline-block mr-4" style={w.accent ? {
        background: 'linear-gradient(135deg, #10D96A 0%, #34D399 50%, #6EE7B7 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
      } : undefined} data-id="element-1357">
            {w.text}
          </motion.span>)}
      </h1>

      <motion.div initial={{
      opacity: 0,
      y: 16,
      filter: 'blur(10px)'
    }} animate={{
      opacity: 1,
      y: 0,
      filter: 'blur(0px)'
    }} transition={{
      delay: 2.7,
      duration: 1.0,
      ease: [0.16, 1, 0.3, 1]
    }} className="mt-10 text-3xl md:text-5xl font-display tracking-tight" data-id="element-1358">
        <span className="text-white/60" data-id="element-1359">it </span>
        <span className="font-bold italic" style={{
        background: 'linear-gradient(135deg, #fbbf24 0%, #f87171 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
      }} data-id="element-1360">
          isn't simple.
        </span>
      </motion.div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: sceneTime > 3.8 ? 0.5 : 0
    }} transition={{
      duration: 0.6
    }} className="mt-16 text-[11px] font-bold tracking-[0.3em] text-white/40 uppercase flex items-center gap-2" data-id="element-1361">
        <span data-id="element-1362">Let's see why</span>
        <motion.span animate={{
        x: [0, 4, 0]
      }} transition={{
        duration: 1.2,
        repeat: Infinity
      }} data-id="element-1363">
          →
        </motion.span>
      </motion.div>
    </motion.div>;
}
// =================== STAGE ===================
function Stage({
  scene,
  elapsed,
  caption,
  captionKey
}: {
  scene: string;
  elapsed: number;
  caption: string;
  captionKey: string;
}) {
  const supportVisible = elapsed >= T.SUPPORT;
  const warnVisible = elapsed >= T.WARN;
  const bounceVisible = elapsed >= T.BOUNCE;
  const impactVisible = elapsed >= T.IMPACT;
  const statsVisible = elapsed >= T.STATS;
  const dividerVisible = elapsed >= T.SUPPORT - 0.4;
  return <motion.div initial={{
    opacity: 0,
    scale: 0.96
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.9,
    ease: [0.16, 1, 0.3, 1]
  }} className="relative" style={{
    width: 'min(100vw, calc(100vh * 16 / 9))',
    height: 'min(100vh, calc(100vw * 9 / 16))',
    maxWidth: '100vw',
    maxHeight: '100vh'
  }} data-id="element-1364">
      <div className="absolute inset-0" data-id="element-1365">
        <div className="absolute" style={{
        width: STAGE_W,
        height: STAGE_H,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%) scale(var(--stage-scale))',
        transformOrigin: 'center center'
      }} data-id="element-1366">
          <ScaleVar data-id="element-1367" />

          {/* TOP CAPTION BAR — sits in top zone, never overlaps cards */}
          <div className="absolute" style={{
          top: 24,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center'
        }} data-id="element-1368">
            <AnimatePresence mode="wait" data-id="element-1369">
              <motion.div key={captionKey} initial={{
              opacity: 0,
              y: 12,
              filter: 'blur(8px)'
            }} animate={{
              opacity: 1,
              y: 0,
              filter: 'blur(0px)'
            }} exit={{
              opacity: 0,
              y: -12,
              filter: 'blur(8px)'
            }} transition={{
              duration: 0.7,
              ease: [0.16, 1, 0.3, 1]
            }} className="px-7 py-2.5 rounded-full bg-white/[0.06] backdrop-blur-2xl border border-white/15 shadow-[0_8px_40px_rgba(0,0,0,0.5)]" data-id="element-1370">
                <p className="font-display text-lg md:text-xl text-white tracking-tight whitespace-nowrap" data-id="element-1371">
                  {caption}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* SVG layer: connection lines + endpoint dots + traveling pulses + bouncing requests.
           z-index 30 lifts lines ABOVE card halos/rings so they remain crisp and visible.
           Lines terminate at card edges so they never paint over card content. */}
          <svg className="absolute pointer-events-none" width={STAGE_W} height={STAGE_H} viewBox={`0 0 ${STAGE_W} ${STAGE_H}`} style={{
          top: 0,
          left: 0,
          overflow: 'visible',
          zIndex: 30
        }} data-id="element-1372">
            <defs data-id="element-1373">
              <linearGradient id="line-grad" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-1374">
                <stop offset="0%" stopColor="#10D96A" stopOpacity="1" data-id="element-1375" />
                <stop offset="100%" stopColor="#34D399" stopOpacity="0.5" data-id="element-1376" />
              </linearGradient>
              <filter id="line-glow" x="-50%" y="-50%" width="200%" height="200%" data-id="element-1377">
                <feGaussianBlur stdDeviation="2.5" result="blur" data-id="element-1378" />
                <feMerge data-id="element-1379">
                  <feMergeNode in="blur" data-id="element-1380" />
                  <feMergeNode in="SourceGraphic" data-id="element-1381" />
                </feMerge>
              </filter>
              <filter id="dot-glow" data-id="element-1382">
                <feGaussianBlur stdDeviation="3" data-id="element-1383" />
              </filter>
              <linearGradient id="divider-grad" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-1384">
                <stop offset="0%" stopColor="#fbbf24" stopOpacity="0" data-id="element-1385" />
                <stop offset="50%" stopColor="#fbbf24" stopOpacity="0.6" data-id="element-1386" />
                <stop offset="100%" stopColor="#fbbf24" stopOpacity="0" data-id="element-1387" />
              </linearGradient>
            </defs>

            {/* Connection lines — STOP at card edges, not centers.
             Uses strokeDasharray/strokeDashoffset (works reliably for ANY orientation,
             including perfectly vertical/horizontal lines).
             NOTE: We deliberately do NOT apply the SVG glow filter to these lines.
             The filter's region is computed from each line's bounding box; a perfectly
             vertical/horizontal line has a zero-width/height bbox, which makes the
             filter clip the line to nothing (invisible). Instead we render a thicker,
             low-opacity backing line behind each one to fake the glow. */}
            {LINES.map(ln => {
            const visible = elapsed >= SYS_TIME[ln.id];
            return <g key={`line-${ln.id}`} data-id="element-1388">
                  {/* Halo / backing line */}
                  <motion.line x1={ln.start.x} y1={ln.start.y} x2={ln.end.x} y2={ln.end.y} stroke="#10D96A" strokeWidth={7} strokeOpacity={0.18} fill="none" strokeLinecap="round" strokeDasharray={ln.length} initial={{
                strokeDashoffset: ln.length,
                opacity: 0
              }} animate={{
                strokeDashoffset: visible ? 0 : ln.length,
                opacity: visible ? 1 : 0
              }} transition={{
                duration: 0.9,
                ease: [0.16, 1, 0.3, 1]
              }} data-id="element-1389" />
                  {/* Crisp foreground line */}
                  <motion.line x1={ln.start.x} y1={ln.start.y} x2={ln.end.x} y2={ln.end.y} stroke="#34D399" strokeWidth={2.5} fill="none" strokeLinecap="round" strokeOpacity={0.95} strokeDasharray={ln.length} initial={{
                strokeDashoffset: ln.length,
                opacity: 0
              }} animate={{
                strokeDashoffset: visible ? 0 : ln.length,
                opacity: visible ? 1 : 0
              }} transition={{
                duration: 0.9,
                ease: [0.16, 1, 0.3, 1]
              }} data-id="element-1390" />
                </g>;
          })}

            {/* Anchor dots at the EDGE of each system card to mark connection clearly */}
            {LINES.map(ln => {
            const visible = elapsed >= SYS_TIME[ln.id];
            return <motion.circle key={`anchor-${ln.id}`} cx={ln.end.x} cy={ln.end.y} r={4.5} fill="#10D96A" filter="url(#dot-glow)" initial={{
              opacity: 0,
              scale: 0
            }} animate={{
              opacity: visible ? 1 : 0,
              scale: visible ? 1 : 0
            }} transition={{
              duration: 0.4,
              delay: visible ? 0.55 : 0
            }} data-id="element-1391" />;
          })}

            {/* Traveling pulses along each line */}
            {LINES.map((ln, i) => {
            const visible = elapsed >= SYS_TIME[ln.id] + 1.2;
            if (!visible) return null;
            return <motion.circle key={`pulse-${ln.id}`} r={3} fill="#10D96A" filter="url(#dot-glow)" animate={{
              cx: [ln.start.x, ln.end.x],
              cy: [ln.start.y, ln.end.y],
              opacity: [0, 1, 1, 0]
            }} transition={{
              duration: 2.2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 0.25
            }} data-id="element-1392" />;
          })}

            {/* Support divider line */}
            {dividerVisible && <motion.line x1={120} y1={SUPPORT_DIVIDER_Y} x2={STAGE_W - 120} y2={SUPPORT_DIVIDER_Y} stroke="url(#divider-grad)" strokeWidth={1} strokeDasharray="2 4" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.6
          }} data-id="element-1393" />}

            {/* Bouncing support requests → AI Agent (warning) */}
            {bounceVisible && SUPPORT_CHIPS.map((chip, i) => {
            const ai = SYSTEMS.find(s => s.id === 'ai')!;
            return <g key={`bounce-${chip.id}`} data-id="element-1394">
                    <motion.path d={`M ${chip.x} ${chip.y} L ${ai.x} ${ai.y}`} stroke="#fbbf24" strokeWidth={1} strokeDasharray="4 6" strokeOpacity={0.35} fill="none" initial={{
                pathLength: 0
              }} animate={{
                pathLength: 1
              }} transition={{
                duration: 0.7,
                delay: i * 0.15
              }} data-id="element-1395" />
                    <motion.circle r={4.5} fill="#fbbf24" filter="url(#dot-glow)" animate={{
                cx: [chip.x, ai.x, chip.x],
                cy: [chip.y, ai.y, chip.y]
              }} transition={{
                duration: 2.4,
                delay: i * 0.4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-1396" />
                  </g>;
          })}
          </svg>

          {/* Support divider label */}
          {dividerVisible && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6
        }} className="absolute" style={{
          top: SUPPORT_DIVIDER_Y - 12,
          left: '50%',
          transform: 'translateX(-50%)'
        }} data-id="element-1397">
              <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/10 border border-amber-400/30 backdrop-blur-xl" data-id="element-1398">
                <AlertTriangle className="w-3 h-3 text-amber-400" data-id="element-1399" />
                <span className="text-[10px] font-bold tracking-[0.22em] text-amber-300 uppercase" data-id="element-1400">
                  Support Layer
                </span>
              </div>
            </motion.div>}

          {/* Center: Your Business */}
          <PositionedNode x={CENTER.x} y={CENTER.y} data-id="element-1401">
            <motion.div initial={{
            scale: 0,
            opacity: 0
          }} animate={{
            scale: elapsed >= T.CENTER_IN ? 1 : 0,
            opacity: elapsed >= T.CENTER_IN ? 1 : 0
          }} transition={{
            type: 'spring',
            stiffness: 220,
            damping: 20
          }} className="relative" data-id="element-1402">
              <motion.div animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 0, 0.5]
            }} transition={{
              duration: 2.4,
              repeat: Infinity,
              ease: 'easeOut'
            }} className="absolute inset-0 rounded-2xl border border-emerald-400/50" data-id="element-1403" />
              <motion.div animate={{
              scale: [1, 1.32, 1],
              opacity: [0.3, 0, 0.3]
            }} transition={{
              duration: 2.4,
              repeat: Infinity,
              ease: 'easeOut',
              delay: 0.8
            }} className="absolute inset-0 rounded-2xl border border-emerald-400/30" data-id="element-1404" />
              <div className="absolute -inset-4 bg-emerald-500/20 rounded-2xl blur-2xl" data-id="element-1405" />

              <div className="relative rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 shadow-[0_0_60px_rgba(16,217,106,0.6)] border border-emerald-300/50 flex items-center gap-3 px-6 py-4" style={{
              width: CENTER_W,
              height: CENTER_H
            }} data-id="element-1406">
                <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center shrink-0" data-id="element-1407">
                  <Building2 className="w-6 h-6 text-white" strokeWidth={2.2} data-id="element-1408" />
                </div>
                <div className="min-w-0" data-id="element-1409">
                  <div className="text-[10px] font-bold tracking-[0.25em] text-white/80 uppercase" data-id="element-1410">
                    Core
                  </div>
                  <div className="text-lg font-semibold text-white tracking-tight leading-tight whitespace-nowrap" data-id="element-1411">
                    Your Business
                  </div>
                </div>
              </div>
            </motion.div>
          </PositionedNode>

          {/* System cards — fixed size matching CARD_W/CARD_H so edge math is exact */}
          {SYSTEMS.map(s => {
          const Icon = s.icon;
          const visible = elapsed >= SYS_TIME[s.id];
          return <PositionedNode key={s.id} x={s.x} y={s.y} data-id="element-1412">
                <motion.div initial={{
              opacity: 0,
              scale: 0.85,
              y: 10
            }} animate={{
              opacity: visible ? 1 : 0,
              scale: visible ? 1 : 0.85,
              y: visible ? 0 : 10
            }} transition={{
              duration: 0.6,
              ease: [0.16, 1, 0.3, 1]
            }} className="relative" style={{
              width: CARD_W,
              height: CARD_H
            }} data-id="element-1413">
                  <div className="absolute -inset-3 bg-emerald-500/[0.10] rounded-2xl blur-xl" data-id="element-1414" />
                  <div className="relative rounded-xl bg-[#0F172A]/95 backdrop-blur-xl border border-white/15 shadow-[0_8px_32px_rgba(0,0,0,0.6)] flex items-center gap-2.5 px-4" style={{
                width: CARD_W,
                height: CARD_H
              }} data-id="element-1415">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-white/15 to-white/[0.03] border border-white/10 flex items-center justify-center shrink-0" data-id="element-1416">
                      <Icon className="w-4 h-4 text-emerald-300" strokeWidth={2} data-id="element-1417" />
                    </div>
                    <div className="min-w-0 flex-1" data-id="element-1418">
                      <div className="text-[13px] font-semibold text-white tracking-tight truncate" data-id="element-1419">
                        {s.label}
                      </div>
                      <div className="text-[10px] text-white/50 font-medium tracking-wide truncate" data-id="element-1420">
                        {s.sub}
                      </div>
                    </div>

                    {s.warning && warnVisible && <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  type: 'spring',
                  stiffness: 300
                }} className="absolute -top-2 -right-2" data-id="element-1421">
                        <motion.div animate={{
                    scale: [1, 1.18, 1],
                    boxShadow: ['0 0 0px rgba(251,191,36,0.5)', '0 0 18px rgba(251,191,36,0.95)', '0 0 0px rgba(251,191,36,0.5)']
                  }} transition={{
                    duration: 1.3,
                    repeat: Infinity
                  }} className="w-6 h-6 rounded-full bg-amber-500 border-2 border-[#0A0F1F] flex items-center justify-center" data-id="element-1422">
                          <AlertTriangle className="w-3 h-3 text-white" strokeWidth={2.5} data-id="element-1423" />
                        </motion.div>
                      </motion.div>}
                  </div>
                </motion.div>
              </PositionedNode>;
        })}

          {/* Cost-of-complexity HUD — bottom-left, well clear of all cards and support layer */}
          <div className="absolute" style={{
          top: STAGE_H - 230,
          left: 24
        }} data-id="element-1424">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: statsVisible ? 1 : 0,
            y: statsVisible ? 0 : 16
          }} transition={{
            duration: 0.7,
            ease: [0.16, 1, 0.3, 1]
          }} className="rounded-2xl bg-white/[0.04] backdrop-blur-xl border border-white/10 p-3.5 shadow-[0_8px_32px_rgba(0,0,0,0.4)]" style={{
            width: 200
          }} data-id="element-1425">
              <div className="flex items-center gap-2 mb-3 pb-2.5 border-b border-white/5" data-id="element-1426">
                <div className="w-1 h-3 rounded-full bg-amber-400" data-id="element-1427" />
                <div className="text-[9px] font-bold tracking-[0.22em] text-white/55 uppercase" data-id="element-1428">
                  Cost of Complexity
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2" data-id="element-1429">
                {PAIN_STATS.map((stat, i) => {
                const Icon = stat.icon;
                return <motion.div key={stat.label} initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: statsVisible ? 1 : 0,
                  y: statsVisible ? 0 : 8
                }} transition={{
                  duration: 0.45,
                  delay: statsVisible ? 0.15 + i * 0.08 : 0
                }} className="px-2 py-1.5 rounded-lg bg-white/[0.03] border border-white/5" data-id="element-1430">
                      <div className="flex items-center gap-1.5 mb-0.5" data-id="element-1431">
                        <Icon className="w-2.5 h-2.5 text-amber-400" data-id="element-1432" />
                        <div className="text-[8px] font-bold tracking-[0.15em] text-white/45 uppercase truncate" data-id="element-1433">
                          {stat.label}
                        </div>
                      </div>
                      <div className="text-sm font-bold text-white tracking-tight" data-id="element-1434">
                        {stat.value}
                      </div>
                    </motion.div>;
              })}
              </div>
            </motion.div>
          </div>

          {/* Support chips */}
          {SUPPORT_CHIPS.map((chip, i) => <PositionedNode key={chip.id} x={chip.x} y={chip.y} data-id="element-1435">
              <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: supportVisible ? 1 : 0,
            y: supportVisible ? 0 : 16
          }} transition={{
            duration: 0.5,
            delay: i * 0.12,
            ease: [0.16, 1, 0.3, 1]
          }} className="flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400/[0.08] backdrop-blur-xl border border-amber-400/30 whitespace-nowrap shadow-[0_0_20px_rgba(251,191,36,0.15)]" data-id="element-1436">
                <Headphones className="w-3.5 h-3.5 text-amber-400" data-id="element-1437" />
                <span className="text-[11px] font-semibold text-amber-100 tracking-wide" data-id="element-1438">
                  {chip.label}
                </span>
              </motion.div>
            </PositionedNode>)}

          {/* Impact takeover — fades back out in the final 1.2s so the
            systems pentagon re-emerges and hands off cleanly to Slide 2 */}
          <AnimatePresence data-id="element-1439">
            {impactVisible && <motion.div initial={{
            opacity: 0
          }} animate={{
            // Fade overlay back to 0 in the last 1.2s of the slide
            opacity: elapsed > TOTAL_DURATION - 1.2 ? Math.max(0, 1 - (elapsed - (TOTAL_DURATION - 1.2)) / 1.2) : 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.9
          }} className="absolute inset-0 flex items-center justify-center" style={{
            background: 'radial-gradient(circle at center, rgba(6,8,15,0.92) 0%, rgba(6,8,15,0.98) 100%)'
          }} data-id="element-1440">
                <motion.div animate={{
              opacity: elapsed > TOTAL_DURATION - 1.4 ? Math.max(0, 1 - (elapsed - (TOTAL_DURATION - 1.4)) / 1.0) : 1
            }} transition={{
              duration: 0.3
            }} data-id="element-1441">
                  <ImpactReveal startTime={T.IMPACT} elapsed={elapsed} data-id="element-1442" />
                </motion.div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>;
}
// =================== Helpers ===================
function ScaleVar() {
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const scale = Math.min(w / STAGE_W, h / STAGE_H, 1);
      document.documentElement.style.setProperty('--stage-scale', String(scale));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return null;
}
function PositionedNode({
  x,
  y,
  children
}: {
  x: number;
  y: number;
  children: React.ReactNode;
}) {
  return <div className="absolute" style={{
    left: x,
    top: y,
    transform: 'translate(-50%, -50%)'
  }} data-id="element-1443">
      {children}
    </div>;
}
function ImpactReveal({
  startTime,
  elapsed
}: {
  startTime: number;
  elapsed: number;
}) {
  const t = elapsed - startTime;
  const words = [{
    text: 'Complex.',
    show: 0.0
  }, {
    text: 'Costly.',
    show: 0.9
  }, {
    text: 'Fragmented.',
    show: 1.8
  }];
  return <div className="text-center px-6" data-id="element-1444">
      <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8
    }} className="flex items-center gap-2 justify-center mb-8" data-id="element-1445">
        <Sparkles className="w-3.5 h-3.5 text-amber-400" data-id="element-1446" />
        <span className="text-[11px] font-bold tracking-[0.3em] text-amber-400 uppercase" data-id="element-1447">
          The bottom line
        </span>
        <Sparkles className="w-3.5 h-3.5 text-amber-400" data-id="element-1448" />
      </motion.div>

      <div className="flex items-baseline justify-center gap-4 md:gap-6 flex-wrap" data-id="element-1449">
        {words.map((w, i) => {
        const visible = t >= w.show;
        return <motion.span key={i} initial={{
          opacity: 0,
          y: 30,
          scale: 0.85,
          filter: 'blur(10px)'
        }} animate={{
          opacity: visible ? 1 : 0,
          y: visible ? 0 : 30,
          scale: visible ? 1 : 0.85,
          filter: visible ? 'blur(0px)' : 'blur(10px)'
        }} transition={{
          duration: 0.9,
          ease: [0.16, 1, 0.3, 1]
        }} className="font-display text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight text-white" style={{
          textShadow: '0 0 30px rgba(251,146,60,0.7), 0 0 60px rgba(239,68,68,0.4), 0 0 90px rgba(239,68,68,0.2)'
        }} data-id="element-1450">
              {w.text}
            </motion.span>;
      })}
      </div>

      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: t >= 2.8 ? 0.7 : 0
    }} transition={{
      duration: 0.8
    }} className="mt-12 text-base md:text-lg text-white/65 font-medium tracking-wide max-w-md mx-auto italic" data-id="element-1451">
        There has to be a better way.
      </motion.p>
    </div>;
}
// Export the total duration so the deck can compute the global timeline
export const SLIDE_1_DURATION = TOTAL_DURATION;